/**
 * The platform directory.
 *
 * One search box answers two different questions: "which festival" and
 * "where is this artist playing". Grouping the results by kind is what lets
 * one input serve both without either feeling like an afterthought.
 *
 * **It is not the front door any more.** A signed-out visitor gets `Welcome`
 * instead — no cards, no counts, no search (`plan.md` §9.1). The list of
 * upcoming festivals is the product, not the advertisement for it.
 *
 * The screen is not the rule: `/festivals` and `/search` require a session at
 * the API, because anyone can call it directly. This branch is what makes that
 * refusal look like a welcome rather than an error.
 */
import { useState, useDeferredValue, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, endpoints, isNetworkFailure } from '@/lib/api';
import { noteAttending } from '@/lib/attendance';
import { Alert } from './authControls';
import { appUrl, festivalUrl, supportUrl } from '@/lib/tenant';
import { askToSignIn } from '@/lib/signInFirst';
import { Card, Empty, ErrorState, LoadingScreen, Row, Spinner, Stack, Txt, flag, localCountry } from '@/ui';
import { useLocale } from '@/app/LocaleProvider';
import { RaveRows, raveTypeName } from './RaveRows';
import { RAVE_TYPES } from '@myrave/shared';
import { useT } from '@/i18n';
import { useAuth, leaveTo } from '@/app/AuthProvider';
import { track } from '@/offline/events';
import { RaveList, RavePage, type Rave } from './raves';
import './Directory.css';
import { FestivalPicker } from '@/app/FestivalPicker';
import { CompleteProfileBanner } from '@/app/CompleteProfileBanner';
import { usePageTitle } from '@/lib/pageTitle';
import { keptFor } from '@/lib/keptRaves';


/**
 * Attend an edition, and count it.
 *
 * The `attend` event fires AFTER the server said yes — a refused attend is not
 * an attend — and is not awaited: nothing on this path may wait on the stats
 * queue. Exported so the "once per attend, never on a refusal" rule has a test
 * that does not need the whole directory on screen.
 */
export async function attendEdition(editionId: string): Promise<void> {
  await endpoints.attend(editionId);
  noteAttending(editionId);
  track(editionId, { type: 'attend' });
}

/**
 * Two pages, on the phone AND the website (the user, 2026-10-27: "the new home page should be
 * also in the web, with the cards — upcoming raves, the new homepage with the cards; full raves
 * list, find a rave"): `upcoming` is `/` — rows that scroll sideways, open to everybody — and
 * `all` is `/raves`, Find a rave: every rave with the country, type and worldwide filters, for
 * an account (the route asks to sign in).
 */
export function Directory({ page = 'all' }: { page?: 'upcoming' | 'all' }) {
  const { user } = useAuth();
  const tr = useT();
  const navigate = useNavigate();
  usePageTitle([page === 'upcoming' ? tr('Upcoming raves') : tr('Find a rave')]);
  /*
   * Seeded from `?q=`, which is how the header's search reaches this page.
   * Without it that control navigated here and dropped the term — the person
   * typed a search, arrived at the search page, and found it empty.
   */
  /*
   * Read from `?q=`, never written here. The app header owns the input; this
   * page renders whatever it was sent. `useState` initialised once would have
   * gone stale the moment somebody searched again from the header without a
   * full reload.
   */
  const [params] = useSearchParams();
  // The phone's first "See all raves" was `/?all=1`; it is `/raves` now.
  const oldSeeAll = page === 'upcoming' && params.get('all') === '1';
  const query = params.get('q') ?? '';
  // Deferred so typing stays smooth while a search is in flight.
  const deferred = useDeferredValue(query);
  const searching = deferred.trim().length >= 2;

  const festivals = useQuery({
    queryKey: ['festivals'],
    queryFn: endpoints.festivals,
    // Open to everybody since 2026-10-26 (the user: "on the first it will show the users the
    // upcoming raves"), and the same for everybody — so asked at once, alongside the session
    // check (2026-10-27: waiting for it drew two loading screens with the page between).
  });
  const results = useQuery({
    queryKey: ['search', deferred],
    queryFn: () => endpoints.search(deferred),
    enabled: searching && Boolean(user),
  });

  /*
   * Which of these is this person already going to?
   *
   * Asked alongside the catalogue rather than folded into it: `/festivals` is
   * the same answer for everybody and caches accordingly, while this is
   * per-account. Merging them would make the public list uncacheable to save
   * one request.
   */
  const mine = useQuery({
    queryKey: ['me', 'festivals'],
    queryFn: endpoints.myFestivals,
    enabled: Boolean(user),
  });
  // A rave you only RUN is in My raves too, but you are not attending it (`attending: false`).
  const attending = new Set((mine.data?.festivals ?? []).filter((f) => f.attending !== false).map((f) => f.editionId));

  const [pending, setPending] = useState<string | null>(null);
  const [attendError, setAttendError] = useState<string | null>(null);
  const attend = async (editionId: string, subdomain: string, editionSlug?: string | null) => {
    // The year that was attended — not whichever one the bare subdomain opens.
    const target = festivalUrl(subdomain, editionSlug);
    // Signed out, the rave just opens — its schedule and map need no account (2026-10-26).
    if (!user) { await leaveTo(target); return; }
    setPending(editionId);
    setAttendError(null);
    try {
      await attendEdition(editionId);
      await mine.refetch();
      // Straight in. Somebody who just chose a rave wants its lineup, not a
      // button that has changed colour.
      await leaveTo(target);
    } catch (err) {
      /*
       * **A paid rave answers 402, and its paywall lives at its own address.**
       *
       * `EditionProvider` already turns the bundle's 402 into `Paywall`, with
       * the price the refusal carries — and attend refuses with the same
       * payload. So the answer is to go there, not to draw a second paywall
       * here. Anything else is said on screen: this used to be `try/finally`
       * with no catch, and a refused Attend looked like a button that did
       * nothing.
       */
      if (err instanceof ApiError && err.status === 402) {
        await leaveTo(target);
        return;
      }
      setAttendError(err instanceof ApiError
        ? err.message
        : tr('Could not reach the server. Check your connection and try again.'));
    } finally {
      setPending(null);
    }
  };

  // No connection: the list is never kept on the phone — the raves kept for offline are shown instead.
  const listOffline = !festivals.data && festivals.isError && isNetworkFailure(festivals.error);
  const keptHere: Rave[] = user ? keptFor(mine.data?.festivals ?? []).map((f) => ({ ...f, attending: true })) : [];

  if (oldSeeAll) return <Navigate to="/raves" replace />;

  return (
    /*
     * The SAME page shell as My raves — same measure, same padding, same
     * heading. It used to cap its content at 560px while the dashboard used
     * the app's 980, so identical cards came out two-up here and three-up
     * there. One object, two sizes, depending on which page you were on.
     */
    <RavePage title={page === 'upcoming' ? tr('Upcoming raves') : tr('Find a rave')}>
      {/* Under the title until the account is complete (the user, 2026-10-20). */}
      <CompleteProfileBanner />
      {/*
        * No search box on the page. Search is in the app header now, on every
        * screen — a second one here would be two inputs doing the same job,
        * six inches apart, and only one of them reachable from anywhere else.
        * `?q=` still lands here and shows results.
        */}
      {attendError && (
        <div className="directory-attend-error">
          <Alert>{tr('Could not add that rave: {reason}', { reason: attendError })}</Alert>
        </div>
      )}
      {/* Festival mode (step 59): a stored or last-opened rave on its dates opens from here too. */}
      {user && !searching && mine.isFetched && <FestivalPicker attending={mine.data?.festivals ?? []} />}
      {searching ? (
        <SearchResults query={deferred} loading={results.isLoading} data={results.data} />
      ) : listOffline && keptHere.length > 0 ? (
        /* No signal: only the raves kept on this phone, to enter (the QA walk, 2026-11-02; `keptRaves.ts`). */
        <>
          <p className="directory-offline" role="status">{tr('No internet connection — only the raves kept on this phone.')}</p>
          <RaveList raves={keptHere} now={Date.now()} />
        </>
      ) : (
        <RaveDirectory
          loading={festivals.isLoading}
          data={festivals.data?.festivals}
          // No connection: the list is never kept on the phone, so say so — never "No raves yet"
          // (the user, 2026-10-27). A server's refusal is not this.
          offline={listOffline}
          retry={() => void festivals.refetch()}
          attending={attending}
          // Signed out there is nothing to attend with — no Attend button; the card opens the rave
          // (the user, 2026-10-26: "we don't need the attend button if the user is not logged in").
          onAttend={user ? attend : undefined}
          pending={pending}
          now={Date.now()}
          view={page === 'upcoming' ? 'rows' : 'all'}
          // See all raves is Find a rave, for an account (the user, 2026-10-26: "on the full Find a
          // rave list, request the user to log in") — signed out, the sign-in, and back to the list.
          onSeeAll={() => {
            if (!user) { askToSignIn({ to: appUrl('/raves') }); return; }
            navigate('/raves');
          }}
        />
      )}
      {/* Under the list: a rave that is not here is a support ticket, its
          topic already picked (the user, 2026-10-21) — for somebody signed in only
          (2026-10-27: "remove the can't find your rave from the homepage while signed out"). */}
      {user && (
        <p className="directory-missing">
          {tr('Can’t find your rave?')}{' '}
          <a href={supportUrl('/?topic=missing-rave')}>{tr('Submit a ticket to us')}</a>
        </p>
      )}
    </RavePage>
  );
}

function SearchResults({
  query, loading, data,
}: { query: string; loading: boolean; data?: Awaited<ReturnType<typeof endpoints.search>> }) {
  const tr = useT();
  if (loading) return <Spinner label={tr('Searching…')} />;
  const performers = data?.performers ?? [];
  const festivals = data?.festivals ?? [];

  if (performers.length === 0 && festivals.length === 0) {
    return (
      <Empty
        title={tr('Nothing found for “{query}”', { query })}
        hint={tr("You can search by rave name, or by an artist's name, country or genre.")}
      />
    );
  }

  return (
    <Stack gap={20} className="directory-results">
      {performers.length > 0 && (
        <Stack gap={8}>
          <SectionLabel>{tr('Artists')}</SectionLabel>
          {performers.map((p) => (
            <Link key={p.id} to={`/artists/${p.slug}`} className="directory-link">
              <Card className="directory-card">
                <Row gap={12}>
                  <div className="directory-avatar" />
                  <Stack gap={3} className="directory-grow">
                    <Row gap={7}>
                      <Txt size={15} weight={700}>{p.name}</Txt>
                      <Txt size={13}>{p.countries.map(flag).join('')}</Txt>
                    </Row>
                    {p.tags.length > 0 && (
                      <Txt size={12} tone="muted">{p.tags.join(' · ')}</Txt>
                    )}
                    <Txt size="badge" weight={600} tone="accent">
                      {p.appearanceCount === 1
                        ? tr('Playing at 1 rave')
                        : tr('Playing at {count} raves', { count: p.appearanceCount })}
                    </Txt>
                  </Stack>
                </Row>
              </Card>
            </Link>
          ))}
        </Stack>
      )}

      {festivals.length > 0 && (
        <Stack gap={8}>
          <SectionLabel>{tr('Raves')}</SectionLabel>
          {festivals.map((f) => (
            <a key={`${f.id}:${f.editionSlug ?? ''}`} href={festivalUrl(f.subdomain, f.editionSlug)} className="directory-link">
              <Card className="directory-card">
                <Stack gap={3}>
                  <Txt size={15} weight={700}>{f.editionName ?? f.name}</Txt>
                  <Txt size={12} tone="muted">
                    {[f.venue, f.actCount > 0 ? tr('{count} acts', { count: f.actCount }) : null].filter(Boolean).join(' · ')}
                  </Txt>
                </Stack>
              </Card>
            </a>
          ))}
        </Stack>
      )}
    </Stack>
  );
}


/**
 * Every rave, on the same card My raves uses.
 *
 * It used to draw its own — a different wash, a different heading font, a
 * date range where the dashboard had counters, and an `Attend` button that
 * stayed `Attend` for a rave you were already going to. One object rendered
 * two ways in one product is how people stop recognising it.
 *
 * **Attendance is known here**, by asking `/me/festivals` alongside the
 * catalogue. The card then says so and the button becomes the way in, rather
 * than inviting somebody to attend something twice.
 */
/**
 * Every rave, on the shared view.
 *
 * This screen knows WHICH raves and who is attending them. `RaveList` knows
 * how a list of raves looks — the filters, the card/table toggle, the sort,
 * both layouts. It used to build all of that here, beside a second copy on
 * the dashboard, and the two had already drifted.
 */
function RaveDirectory({
  loading, data, attending, onAttend, pending, now, view = 'list', onSeeAll, offline, retry,
}: {
  /** `rows` — Upcoming raves; `all` — Find a rave, the list with its filters; `list` — the list alone. */
  view?: 'list' | 'rows' | 'all';
  onSeeAll?: () => void;
  loading: boolean;
  data?: Awaited<ReturnType<typeof endpoints.festivals>>['festivals'];
  /** The list could not be asked for — no connection. */
  offline?: boolean;
  retry?: () => void;
  attending: Set<string>;
  onAttend?: (editionId: string, subdomain: string, editionSlug?: string | null) => void;
  pending: string | null;
  now: number;
}) {
  const tr = useT();
  const [country, setCountry] = useState('');
  const [type, setType] = useState('');
  // The reader's country (the app's region — chosen, else where they are): a row, and a filter.
  const { locale, region } = useLocale();
  const home = /^[A-Z]{2}$/i.test(region) ? { code: region.toUpperCase(), name: localCountry(locale, region) ?? region } : null;
  const [where, setWhere] = useState<'world' | 'home'>('world');
  // The same wait My raves shows. They are one view and must not
  // disagree about how they load.
  if (loading) return <LoadingScreen label={tr('Loading raves…')} />;
  if (offline) {
    return <ErrorState title={tr('You are offline')} detail={tr('Connect to the internet to see the raves.')} retry={retry} retryLabel={tr('Try again')} retryVariant="primary" />;
  }

  /*
   * One card per EDITION — the API sends one entry per published year, so
   * OZORA 2027 is a new rave here and OZORA 2026 a past one (hidden until
   * "Past" is chosen), each linking to its own year.
   */
  const raves: Rave[] = (data ?? [])
    .filter((f) => f.edition)
    .map((f) => ({
      editionId: f.edition!.id,
      editionSlug: f.edition!.slug,
      editionName: f.edition!.name,
      festivalName: f.name,
      subdomain: f.subdomain,
      venue: f.edition!.venue,
      country: f.country,
      crowdSize: f.crowdSize ?? null,
      test: f.edition!.test === true,
      types: f.types ?? [],
      timezone: f.edition!.timezone,
      gatesOpenAt: f.edition!.gatesOpenAt,
      endsAt: f.edition!.endsAt,
      logoUrl: f.logoUrl,
      coverUrl: f.coverUrl,
      actCount: f.edition!.actCount,
      stageCount: f.edition!.stageCount,
      countdown: f.edition!.countdown,
      attending: attending.has(f.edition!.id),
    }));

  const empty = <Empty title={tr('No raves yet')} hint={tr('When a rave is published it will appear here.')} />;
  if (view === 'rows') {
    return (
      <RaveRows raves={raves} now={now} busyId={pending} empty={empty} onSeeAll={() => onSeeAll?.()} home={home}
        onAttend={onAttend ? (r) => onAttend(r.editionId, r.subdomain, r.editionSlug) : undefined} />
    );
  }
  return (
    <>
      {view === 'all' && (
        <div className="directory-filters">
          {/* In my country or worldwide (the user, 2026-10-26). */}
          {home && (
            <div className="directory-where" role="group" aria-label={tr('Where')}>
              <button type="button" className="directory-where-opt" aria-pressed={where === 'world'} onClick={() => setWhere('world')}>{tr('Worldwide')}</button>
              <button type="button" className="directory-where-opt" aria-pressed={where === 'home'} onClick={() => setWhere('home')}>{tr('In {country}', { country: home.name })}</button>
            </div>
          )}
          <CountryFilter raves={raves} value={country} onChange={setCountry} />
          <TypeFilter raves={raves} value={type} onChange={setType} />
        </div>
      )}
      <RaveList
        raves={view === 'all'
          ? raves.filter((r) => (!country || (r.country ?? '').toUpperCase() === country) && (!type || (r.types ?? []).includes(type))
            && (where === 'world' || !home || (r.country ?? '').toUpperCase() === home.code))
          : raves}
        now={now}
        onAttend={onAttend}
        busyId={pending}
        empty={empty}
      />
    </>
  );
}

/** Find a rave: one type of rave at a time — only the types some rave has (2026-10-26). */
function TypeFilter({ raves, value, onChange }: { raves: readonly Rave[]; value: string; onChange: (type: string) => void }) {
  const tr = useT();
  const have = new Set(raves.flatMap((r) => r.types ?? []));
  const types = RAVE_TYPES.filter((t) => have.has(t));
  if (types.length === 0) return null;
  return (
    <label className="directory-country">
      <span className="directory-country-label">{tr('Type')}</span>
      <select className="directory-country-select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{tr('All types')}</option>
        {types.map((t) => <option key={t} value={t}>{raveTypeName(tr, t)}</option>)}
      </select>
    </label>
  );
}

/** Find a rave: one country at a time, named in the reader's language (2026-10-26). */
function CountryFilter({ raves, value, onChange }: { raves: readonly Rave[]; value: string; onChange: (country: string) => void }) {
  const tr = useT();
  const { locale } = useLocale();
  const countries = [...new Set(raves.map((r) => (r.country ?? '').toUpperCase()).filter((c) => /^[A-Z]{2}$/.test(c)))]
    .map((code) => ({ code, name: localCountry(locale, code) ?? code }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
  return (
    <label className="directory-country">
      <span className="directory-country-label">{tr('Country')}</span>
      <select className="directory-country-select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{tr('All countries')}</option>
        {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
      </select>
    </label>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <Txt as="div" size="badge" weight={700} track={0.09} tone="muted" upper>{children}</Txt>;
}
